import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { useAuth } from "../../sdk";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
const Default_date = dayjs().startOf("hour");

const generateHourlyTimes = () => {
  const times = [];
  for (let h = 0; h < 24; h++) {
    times.push(dayjs().hour(h).minute(0).format("HH:mm"));
  }
  return times;
};

const defaultBatchValues = {
  name: "",
  minTime: Default_date,
  maxTime: Default_date,
};

const convertDateToIsoFormat = (date) => {
  if (date) {
    return dayjs(date).tz("Asia/Kolkata").format("YYYY-MM-DDTHH:mm:ssZ");
  } else {
    return "";
  }
};

const useBatchSchedules = () => {
  const { authFetch } = useAuth();
  const { hotelId } = useParams();
  const [batches, setBatches] = useState();
  const [schedules, setSchedules] = useState();
  const [remainingSchedules, setRemainingSchedules] = useState();
  const [addNewBatch, setAddNewBatch] = useState(false);
  const [newBatchDetails, setNewBatchDetails] = useState(defaultBatchValues);
  const [isBatchesLoading, setIsBatchesLoading] = useState(false);
  const [showSnackbarMsg, setShowSnackbarMsg] = useState({
    open: false,
    isError: false,
    message: "",
  });
  const [showConfirmationDialog, setShowConfirmationDialog] = useState({
    open: false,
    isBatchDelete: false,
    batchId: "",
    scheduleId: "",
  });
  const hourlyOptions = generateHourlyTimes();

  const handleCloseConfirmationDialog = useCallback(() => {
    setShowConfirmationDialog((prev) => ({ ...prev, open: false }));
  }, []);

  const getScheduleBatches = useCallback(async () => {
    setIsBatchesLoading(true);
    try {
      const { get } = await authFetch({
        path: `/hotel/${hotelId}/schedule/batch`,
      });

      const { data, response } = await get();
      if (data && response.ok) {
        setBatches(data);
      }
    } catch (error) {
      console.error("Failed to fetch Batches", error);
    } finally {
      setIsBatchesLoading(false);
    }
  }, [authFetch, hotelId]);

  const getSchedules = useCallback(
    async (isBatchScreen = false) => {
      try {
        const { get } = await authFetch({
          path: `/hotel/${hotelId}/rate-shop/schedule?batchScreen=${isBatchScreen}`,
        });

        const { data, response } = await get();
        if (data && response.ok) {
          if (isBatchScreen) {
            setRemainingSchedules(data?.scheduledRateShops);
          } else {
            setSchedules(data?.scheduledRateShops);
          }
        }
      } catch (error) {
        console.error("Failed to Fetch Schedules", error);
      }
    },
    [authFetch, hotelId]
  );

  useEffect(() => {
    getScheduleBatches();
    getSchedules();
    getSchedules(true);
  }, [getScheduleBatches, getSchedules]);

  const createNewBatch = useCallback(async () => {
    const payload = {
      name: newBatchDetails?.name,
      minTime: convertDateToIsoFormat(newBatchDetails?.minTime),
      maxTime: convertDateToIsoFormat(newBatchDetails?.maxTime),
    };
    try {
      const { post } = await authFetch({
        path: `/hotel/${hotelId}/schedule/batch`,
      });

      const { data, response } = await post(payload);
      if (response.ok) {
        getScheduleBatches();
        setShowSnackbarMsg({
          open: true,
          isError: false,
          message: data || "Batch Created Successfully",
        });
        setAddNewBatch(false);
      } else {
        setShowSnackbarMsg({
          open: true,
          isError: true,
          message: data?.messageToUser || "Error in creating new batch",
        });
      }
    } catch (error) {
      console.error("Failed to create batch", error);
      setShowSnackbarMsg({
        open: true,
        isError: true,
        message: "Error in creating new batch",
      });
    }
  }, [
    authFetch,
    getScheduleBatches,
    hotelId,
    newBatchDetails.maxTime,
    newBatchDetails.minTime,
    newBatchDetails.name,
  ]);

  const deleteBatch = useCallback(
    async (batchId) => {
      try {
        const { del } = await authFetch({
          path: `/hotel/${hotelId}/schedule/batch/${batchId}`,
        });

        const { data, response } = await del();
        if (response.ok) {
          setShowSnackbarMsg({
            open: true,
            isError: false,
            message: data || "Batch Deleted Successfully",
          });
          getScheduleBatches();
        } else {
          setShowSnackbarMsg({
            open: true,
            isError: true,
            message: data?.messageToUser || "Error in deleting batch",
          });
        }
      } catch (error) {
        console.error("Failed to delete batch", error);
        setShowSnackbarMsg({
          open: true,
          isError: true,
          message: "Error in deleting batch",
        });
      } finally {
        handleCloseConfirmationDialog();
      }
    },
    [authFetch, getScheduleBatches, handleCloseConfirmationDialog, hotelId]
  );

  const addScheduleInBatch = useCallback(
    async (batchId, schedules) => {
      const schedulesList = schedules.map((item) => item?.scheduledId);
      const payload = {
        scheduleId: schedulesList,
      };
      try {
        const { post } = await authFetch({
          path: `/hotel/${hotelId}/schedule/batch/${batchId}`,
        });

        const { data, response } = await post(payload);
        if (response.ok) {
          getScheduleBatches();
          getSchedules(true);
          setShowSnackbarMsg({
            open: true,
            isError: false,
            message: data || "Schedules added in batch successfully",
          });
        } else {
          setShowSnackbarMsg({
            open: true,
            isError: true,
            message: data?.messageToUser || "Error in adding schedule in batch",
          });
        }
      } catch (error) {
        console.error(error);
        setShowSnackbarMsg({
          open: true,
          isError: true,
          message: "Error in adding schedule in batch",
        });
      }
    },
    [authFetch, getScheduleBatches, getSchedules, hotelId]
  );

  const removeScheduleFromBatch = useCallback(
    async (batchId, scheduleId) => {
      const payload = {
        scheduleId,
        batchId,
      };
      try {
        const { put } = await authFetch({
          path: `/hotel/${hotelId}/batch/remove-schedule`,
        });

        const { data, response } = await put(payload);
        if (response.ok) {
          getScheduleBatches();
          getSchedules(true);
          setShowSnackbarMsg({
            open: true,
            isError: false,
            message: data || "Schedule removed from batch successfully",
          });
        } else {
          setShowSnackbarMsg({
            open: true,
            isError: true,
            message:
              data?.messageToUser ||
              "Error in removing schedule from batch successfully",
          });
        }
      } catch (error) {
        console.error("Failed to remove schedule from batch");
        setShowSnackbarMsg({
          open: true,
          isError: false,
          message: "Error in removing schedule from batch successfully",
        });
      } finally {
        handleCloseConfirmationDialog();
      }
    },
    [
      authFetch,
      getScheduleBatches,
      getSchedules,
      handleCloseConfirmationDialog,
      hotelId,
    ]
  );

  const updateBatchDetails = useCallback(
    async (batch) => {
      const payload = {
        id: batch?.id,
        name: batch?.name,
        minTime: convertDateToIsoFormat(batch?.minTime),
        maxTime: convertDateToIsoFormat(batch?.maxTime),
      };
      try {
        const { put } = await authFetch({
          path: `/hotel/${hotelId}/schedule/batch`,
        });

        const { data, response } = await put(payload);
        if (response.ok) {
          getScheduleBatches();
          setShowSnackbarMsg({
            open: true,
            isError: false,
            message: data || "Batch Details updated successfully",
          });
        } else {
          setShowSnackbarMsg({
            open: true,
            isError: true,
            message: data?.messageToUser || "Error in updating batch details",
          });
        }
      } catch (error) {
        console.error("Failed to update the batch details", error);
        setShowSnackbarMsg({
          open: true,
          isError: true,
          message: "Error in updating batch details",
        });
      }
    },
    [authFetch, getScheduleBatches, hotelId]
  );

  const handleCreateNewBatch = useCallback(() => {
    createNewBatch();
  }, [createNewBatch]);

  const handleDeleteBatch = useCallback(
    async (batchId) => {
      deleteBatch(batchId);
    },
    [deleteBatch]
  );

  const handleChangeNewBatchDetails = useCallback((value, key) => {
    setNewBatchDetails((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleAddSchedulesInBatch = useCallback(
    (batchId, schedules) => {
      addScheduleInBatch(batchId, schedules);
    },
    [addScheduleInBatch]
  );

  const handleRemoveSchedulesFromBatch = useCallback(
    (batchId, scheduleId) => {
      removeScheduleFromBatch(batchId, scheduleId);
    },
    [removeScheduleFromBatch]
  );

  const handleChangeBatchDetails = useCallback((batchId, key, value) => {
    setBatches((prev) => {
      return prev.map((item) =>
        item.id === batchId ? { ...item, [key]: value } : item
      );
    });
  }, []);

  const handleUpdateBatchDetails = useCallback(
    (batch) => {
      updateBatchDetails(batch);
    },
    [updateBatchDetails]
  );

  return {
    batches,
    schedules,
    remainingSchedules,
    hourlyOptions,
    addNewBatch,
    setAddNewBatch,
    newBatchDetails,
    handleCreateNewBatch,
    handleDeleteBatch,
    handleAddSchedulesInBatch,
    handleRemoveSchedulesFromBatch,
    handleChangeNewBatchDetails,
    isBatchesLoading,
    showSnackbarMsg,
    setShowSnackbarMsg,
    handleChangeBatchDetails,
    handleUpdateBatchDetails,
    showConfirmationDialog,
    setShowConfirmationDialog,
    handleCloseConfirmationDialog,
  };
};

export default useBatchSchedules;
